import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import { Search, Copy, FileDown, Eye, ChevronLeft, ChevronRight, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/store/auth.store'
import { financeService } from '@/services/financeService'
import { reportService, AgedPayablesReportResponse } from '@/services/reportService'

export const AgedPayablesReport: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const { user } = useAuthStore()

  const [reportData, setReportData] = useState<AgedPayablesReportResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [vendors, setVendors] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  // Pagination states
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Filter states
  const [agingDate, setAgingDate] = useState<string>('')
  const [selectedVendorId, setSelectedVendorId] = useState<number | undefined>(undefined)
  
  // Get branch ID from user's selected branch
  const selectedBranchId = user?.selectedBranch?.branchId

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    billNo: true,
    vendor: true,
    date: true,
    dueDate: true,
    notDues: true,
    billDues: true,
    days_0_30: true,
    days_31_60: true,
    days_61_90: true,
    days_91_180: true,
    days_181_360: true,
    days_360_Plus: true,
    total: true,
  })

  // Load vendors when branch is available
  useEffect(() => {
    if (!selectedBranchId) {
      setVendors([])
      setSelectedVendorId(undefined)
      return
    }

    const loadVendors = async () => {
      try {
        const vendorList = await financeService.getAllVendorsForVoucher(selectedBranchId)
        setVendors(vendorList || [])
        // Reset vendor selection to "All" when branch changes
        setSelectedVendorId(undefined)
      } catch (error: any) {
        setVendors([])
      }
    }
    loadVendors()
  }, [selectedBranchId])

  // Set default aging date (today)
  useEffect(() => {
    const today = new Date()
    setAgingDate(formatDateForInput(today))
  }, [])

  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString || dateString === '-') return '-'
    try {
      // Handle different date formats
      const date = new Date(dateString)
      if (isNaN(date.getTime())) {
        // Try parsing DD-MMM-YYYY format
        const parts = dateString.split('-')
        if (parts.length === 3) {
          const months: Record<string, number> = {
            'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
            'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
          }
          const month = months[parts[1]]
          if (month !== undefined) {
            const day = parseInt(parts[0])
            const year = parseInt(parts[2])
            const parsedDate = new Date(year, month, day)
            return parsedDate.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          }
        }
        return dateString
      }
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleSearch = async (resetPage: boolean = false) => {
    if (!selectedBranchId) {
      toast.error('Please select a branch')
      return
    }

    if (!agingDate) {
      toast.error('Please select an aging date')
      return
    }

    try {
      setLoading(true)
      // Convert date to ISO date-time format (YYYY-MM-DDTHH:mm:ss) - without Z for backend compatibility
      const agingDateTime = `${agingDate}T00:00:00`
      
      // If vendor is not selected (All), send vendorId as 0
      const vendorId = selectedVendorId !== undefined ? selectedVendorId : 0
      
      const currentPage = resetPage ? 1 : pageNumber
      if (resetPage) {
        setPageNumber(1)
      }
      
      const data = await reportService.getAgedPayablesReport({
        agingDate: agingDateTime,
        branchId: selectedBranchId,
        pageNumber: currentPage,
        pageSize: pageSize,
        vendorId: vendorId,
      })
      
      setReportData(data)
      setTotalCount(data.totalRecords || data.rows?.length || 0)
      const pages = data.totalRecords ? Math.ceil(data.totalRecords / pageSize) : (data.rows?.length ? Math.ceil(data.rows.length / pageSize) : 0)
      setTotalPages(pages)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load aged payables report')
      setReportData(null)
      setTotalCount(0)
      setTotalPages(0)
    } finally {
      setLoading(false)
    }
  }

  // Handle page size change
  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1)
    // Trigger search with new page size
    if (selectedBranchId && agingDate) {
      setTimeout(() => handleSearch(true), 0)
    }
  }

  // Handle page change
  useEffect(() => {
    if (selectedBranchId && agingDate && pageNumber > 1 && reportData?.rows && reportData.rows.length > 0) {
      handleSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber])

  const filteredData = useMemo(() => {
    if (!reportData?.rows) return []
    if (!searchQuery.trim()) return reportData.rows
    const query = searchQuery.toLowerCase()
    return reportData.rows.filter((item) => {
      return (
        (item.billNo || '').toLowerCase().includes(query) ||
        (item.vendorName || '').toLowerCase().includes(query)
      )
    })
  }, [reportData, searchQuery])

  const handleVendorChange = (value: string) => {
    // When "All" is selected, set to undefined so we can send 0 to API
    setSelectedVendorId(value === 'all' ? undefined : Number(value))
  }

  const handleCopy = () => {
    if (!reportData || filteredData.length === 0) {
      toast.error('No data to copy')
      return
    }

    // Create CSV-like text
    const headers = [
      'Bill No',
      'Vendor',
      'Date',
      'Due Date',
      'Not Dues',
      'Bill Dues',
      '0-30 Days',
      '31-60 Days',
      '61-90 Days',
      '91-180 Days',
      '181-360 Days',
      '360 Above Days',
      'Total',
    ].join('\t')

    const rows = filteredData.map(item => [
      item.billNo,
      item.vendorName,
      item.billDate,
      item.dueDate,
      formatAmount(item.notDues),
      formatAmount(item.dues),
      formatAmount(item.days0To30),
      formatAmount(item.days31To60),
      formatAmount(item.days61To90),
      formatAmount(item.days91To180),
      formatAmount(item.days181To360),
      formatAmount(item.days360Plus),
      formatAmount(item.total),
    ].join('\t'))

    const csvContent = [headers, ...rows].join('\n')

    navigator.clipboard.writeText(csvContent)
      .then(() => toast.success('Data copied to clipboard'))
      .catch(() => toast.error('Failed to copy data'))
  }

  const handleCsv = () => {
    if (!reportData || filteredData.length === 0) {
      toast.error('No data to export')
      return
    }

    // Create CSV content
    const headers = [
      'Bill No',
      'Vendor',
      'Date',
      'Due Date',
      'Not Dues',
      'Bill Dues',
      '0-30 Days',
      '31-60 Days',
      '61-90 Days',
      '91-180 Days',
      '181-360 Days',
      '360 Above Days',
      'Total',
    ].join(',')

    const rows = filteredData.map(item => [
      `"${item.billNo}"`,
      `"${item.vendorName}"`,
      `"${item.billDate}"`,
      `"${item.dueDate}"`,
      formatAmount(item.notDues),
      formatAmount(item.dues),
      formatAmount(item.days0To30),
      formatAmount(item.days31To60),
      formatAmount(item.days61To90),
      formatAmount(item.days91To180),
      formatAmount(item.days181To360),
      formatAmount(item.days360Plus),
      formatAmount(item.total),
    ].join(','))

    const csvContent = [headers, ...rows].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `aged-payables-${agingDate || 'report'}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('CSV file downloaded')
  }

  const handlePrint = () => {
    if (!selectedBranchId || !agingDate) {
      toast.error('Please select branch and aging date before printing')
      return
    }
    
    // Navigate to print page with query parameters
    const params = new URLSearchParams({
      agingDate,
      branchId: String(selectedBranchId),
    })
    if (selectedVendorId) {
      params.append('vendorId', String(selectedVendorId))
    }
    
    window.open(`/reports/aged-payables/print?${params.toString()}`, '_blank')
  }

  const toggleColumn = (columnKey: string) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }))
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.agedPayablesReport')}
        description={t('reports.viewAgedPayables') || 'View aged payables report by bill'}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.agingDate')}</Label>
              <Input
                type="date"
                value={agingDate}
                onChange={(e) => setAgingDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.vendor')}</Label>
              <Select
                value={selectedVendorId ? String(selectedVendorId) : 'all'}
                onValueChange={handleVendorChange}
                disabled={!selectedBranchId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('reports.allVendors')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('reports.allVendors')}</SelectItem>
                  {vendors.map(vendor => {
                    const vendorId = vendor.vendorId || vendor.id || 0
                    const vendorName = vendor.vendorNameEn || vendor.vendorNameAr || vendor.nameEn || vendor.nameAr || `Vendor ${vendorId}`
                    return (
                      <SelectItem key={vendorId} value={String(vendorId)}>
                        {vendorName}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleSearch(true)} disabled={loading}>
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Eye className="h-4 w-4 mr-2" />
                    Column Visibility
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {Object.entries(visibleColumns).map(([key, visible]) => (
                    <DropdownMenuCheckboxItem
                      key={key}
                      checked={visible}
                      onCheckedChange={() => toggleColumn(key)}
                    >
                      {key === 'billNo' ? 'Bill No' :
                       key === 'vendor' ? 'Vendor' :
                       key === 'date' ? 'Date' :
                       key === 'dueDate' ? 'Due Date' :
                       key === 'notDues' ? 'Not Dues' :
                       key === 'billDues' ? 'Bill Dues' :
                       key === 'days_0_30' ? '0-30 Days' :
                       key === 'days_31_60' ? '31-60 Days' :
                       key === 'days_61_90' ? '61-90 Days' :
                       key === 'days_91_180' ? '91-180 Days' :
                       key === 'days_181_360' ? '181-360 Days' :
                       key === 'days_360_Plus' ? '360 Above Days' :
                       key === 'total' ? 'Total' : key}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                {t('reports.print')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                {t('reports.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('reports.csv')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading report...</p>
            </div>
          ) : !reportData || filteredData.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title="No data found"
                description="Click Search to load aged payables report"
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className="border-collapse">
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 bg-primary/5">
                      {visibleColumns.billNo && (
                        <TableHead className={`font-bold text-primary border border-gray-300 px-2 py-2 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.billNo')}</TableHead>
                      )}
                      {visibleColumns.vendor && (
                        <TableHead className={`font-bold text-primary border border-gray-300 px-2 py-2 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.vendor')}</TableHead>
                      )}
                      {visibleColumns.date && (
                        <TableHead className={`font-bold text-primary border border-gray-300 px-2 py-2 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.date')}</TableHead>
                      )}
                      {visibleColumns.dueDate && (
                        <TableHead className={`font-bold text-primary border border-gray-300 px-2 py-2 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.dueDate')}</TableHead>
                      )}
                      {visibleColumns.notDues && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 px-2 py-2">{t('reports.notDues')}</TableHead>
                      )}
                      {visibleColumns.billDues && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 px-2 py-2">{t('reports.billDues')}</TableHead>
                      )}
                      {visibleColumns.days_0_30 && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 px-2 py-2">{t('reports.days_0_30')}</TableHead>
                      )}
                      {visibleColumns.days_31_60 && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 px-2 py-2">{t('reports.days_31_60')}</TableHead>
                      )}
                      {visibleColumns.days_61_90 && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 px-2 py-2">{t('reports.days_61_90')}</TableHead>
                      )}
                      {visibleColumns.days_91_180 && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 px-2 py-2">{t('reports.days_91_180')}</TableHead>
                      )}
                      {visibleColumns.days_181_360 && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 px-2 py-2">{t('reports.days_181_360')}</TableHead>
                      )}
                      {visibleColumns.days_360_Plus && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 px-2 py-2">{t('reports.days_360_Plus')}</TableHead>
                      )}
                      {visibleColumns.total && (
                        <TableHead className="font-bold text-primary text-right border border-gray-300 px-2 py-2">{t('reports.total')}</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((item, index) => (
                      <TableRow
                        key={index}
                        className="border-b hover:bg-primary/5 transition-colors"
                      >
                        {visibleColumns.billNo && (
                          <TableCell className="border border-gray-300 px-2 py-2">{item.billNo}</TableCell>
                        )}
                        {visibleColumns.vendor && (
                          <TableCell className="border border-gray-300 px-2 py-2">{item.vendorName}</TableCell>
                        )}
                        {visibleColumns.date && (
                          <TableCell className="border border-gray-300 px-2 py-2">{formatDate(item.billDate)}</TableCell>
                        )}
                        {visibleColumns.dueDate && (
                          <TableCell className="border border-gray-300 px-2 py-2">{formatDate(item.dueDate)}</TableCell>
                        )}
                        {visibleColumns.notDues && (
                          <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(item.notDues)}</TableCell>
                        )}
                        {visibleColumns.billDues && (
                          <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(item.dues)}</TableCell>
                        )}
                        {visibleColumns.days_0_30 && (
                          <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(item.days0To30)}</TableCell>
                        )}
                        {visibleColumns.days_31_60 && (
                          <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(item.days31To60)}</TableCell>
                        )}
                        {visibleColumns.days_61_90 && (
                          <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(item.days61To90)}</TableCell>
                        )}
                        {visibleColumns.days_91_180 && (
                          <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(item.days91To180)}</TableCell>
                        )}
                        {visibleColumns.days_181_360 && (
                          <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(item.days181To360)}</TableCell>
                        )}
                        {visibleColumns.days_360_Plus && (
                          <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(item.days360Plus)}</TableCell>
                        )}
                        {visibleColumns.total && (
                          <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(item.total)}</TableCell>
                        )}
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    {reportData && (() => {
                      const visibleCount = Object.values(visibleColumns).filter(v => v).length
                      const amountColumnsCount = [
                        visibleColumns.notDues,
                        visibleColumns.billDues,
                        visibleColumns.days_0_30,
                        visibleColumns.days_31_60,
                        visibleColumns.days_61_90,
                        visibleColumns.days_91_180,
                        visibleColumns.days_181_360,
                        visibleColumns.days_360_Plus,
                        visibleColumns.total,
                      ].filter(v => v).length
                      const textColumnsCount = visibleCount - amountColumnsCount
                      
                      return (
                        <TableRow className="border-t-2 border-primary/20 bg-primary/5 font-bold">
                          <TableCell colSpan={textColumnsCount} className="text-right border border-gray-300 px-2 py-2">
                            Total
                          </TableCell>
                          {visibleColumns.notDues && (
                            <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(reportData.totalNotDues)}</TableCell>
                          )}
                          {visibleColumns.billDues && (
                            <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(reportData.totalDues)}</TableCell>
                          )}
                          {visibleColumns.days_0_30 && (
                            <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(reportData.total0To30)}</TableCell>
                          )}
                          {visibleColumns.days_31_60 && (
                            <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(reportData.total31To60)}</TableCell>
                          )}
                          {visibleColumns.days_61_90 && (
                            <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(reportData.total61To90)}</TableCell>
                          )}
                          {visibleColumns.days_91_180 && (
                            <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(reportData.total91To180)}</TableCell>
                          )}
                          {visibleColumns.days_181_360 && (
                            <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(reportData.total181To360)}</TableCell>
                          )}
                          {visibleColumns.days_360_Plus && (
                            <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(reportData.total360Plus)}</TableCell>
                          )}
                          {visibleColumns.total && (
                            <TableCell className="text-right border border-gray-300 px-2 py-2">{formatAmount(reportData.grandTotal)}</TableCell>
                          )}
                        </TableRow>
                      )
                    })()}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

